import React, { useState } from 'react';
import { FileRecord, Category } from '@twinsketch/topika-model';
import FileService from 'src/services/FileService';
import { FaDownload, FaEye, FaTrash } from 'react-icons/fa';

interface MaterialFileSectionProps {
  files: FileRecord[];
  materialId: string;
  onFileUploaded: (file: FileRecord) => void;
  onFileDeleted: (fileId: string) => void;
}

const MaterialFileSection: React.FC<MaterialFileSectionProps> = ({
  files,
  materialId,
  onFileUploaded,
  onFileDeleted,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<'image' | 'pdf' | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [erro, setError] = useState<string | null>(null);

  const isImageFile = (fileName: string) => {
    return ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'].some(ext =>
      fileName.toLowerCase().endsWith(ext)
    );
  };

  const isPdfFile = (fileName: string) => fileName.toLowerCase().endsWith('.pdf');

  const handlePreview = (file: FileRecord) => {
    if (isImageFile(file.fileName)) {
      setPreviewType('image');
      setPreviewUrl(file.s3Url);
    } else if (isPdfFile(file.fileName)) {
      setPreviewType('pdf');
      setPreviewUrl(file.s3Url);
    } else {
      setPreviewType(null);
      setPreviewUrl(null);
    }
  };

  const handleDownloadFile = async fileName => {
    try {
      const file = files.find(f => f.fileName === fileName);
      const response = await fetch(file.s3Url);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}-${materialId}`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      setError('Download failed.');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    if (!selectedFile) return;

    if (isPdfFile(selectedFile.name) || isImageFile(selectedFile.name)) {
      setFile(selectedFile);
      setUploadSuccess(false);
    } else {
      alert('Only image or PDF files are supported.');
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await FileService.deleteFile(fileId, materialId);
      onFileDeleted(fileId);
    } catch {
      setError('Failed to delete file.');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      const message = 'Please select a file.';
      setUploadError(message);
      alert(message);
      return;
    }

    try {
      setUploading(true);
      const uploadedFileRecord = await FileService.uploadFileWithPresignedUrl(
        file,
        'Material',
        materialId,
        notes,
        Category.UNDEFINED,
        []
      );
      onFileUploaded(uploadedFileRecord); // ✅ Notify parent of new file

      // Reset input state
      setFile(null);
      setNotes('');
      setUploadError(null);
      setUploadSuccess(true);
    } catch (err) {
      console.error('Error uploading file:', err);

      let errorMessage = 'Failed to upload the file.';
      if ((err as any).response) {
        errorMessage =
          (err as any).response.data.message || JSON.stringify((err as any).response.data);
      } else if ((err as any).message) {
        errorMessage = (err as any).message;
      }

      setUploadError(errorMessage);
      alert(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="file-tab-section">
      <h3 style={{ marginBottom: '8px' }}>관련 파일</h3>
      <div style={{ height: '3px', backgroundColor: '#ddd', margin: '16px 0' }} />

      {/* Upload Section */}
      <div style={{ marginBottom: '16px' }}>
        <input type="file" accept="image/*,.pdf" onChange={handleFileChange} />
        {file && (
          <>
            <p style={{ marginTop: '8px' }}>Selected File: {file.name}</p>
            <input
              type="text"
              placeholder="파일에 대한 메모 (선택)"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ marginTop: '8px', width: '100%' }}
            />
            <div style={{ marginTop: '12px', display: 'flex', gap: '10px' }}>
              <button
                onClick={handleUpload}
                disabled={uploading}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#4CAF50',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                onClick={() => {
                  setFile(null);
                  setNotes('');
                  setUploadSuccess(false);
                }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#ccc',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Clear
              </button>
            </div>
            {uploadSuccess && <p style={{ color: 'green', marginTop: '10px' }}>업로드 성공!</p>}
          </>
        )}
      </div>

      {/* File List */}
      {files.length === 0 ? (
        <p style={{ fontStyle: 'italic' }}>첨부된 파일이 없습니다.</p>
      ) : (
        <ul className="file-list-section">
          {files.map(file => (
            <li key={file.id} className="file-item-section">
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span
                    className="file-link-name"
                    style={{ textDecoration: 'underline', color: '#0077cc', cursor: 'pointer' }}
                    onClick={() => handlePreview(file)}
                  >
                    {file.fileName}
                  </span>
                  {file.notes && <span className="file-note">{file.notes}</span>}
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <FaEye
                    size={12}
                    style={{ cursor: 'pointer', color: '#555', opacity: 0.8 }}
                    title="Preview"
                    onClick={() => handlePreview(file)}
                  />
                  <FaDownload
                    size={12}
                    style={{ cursor: 'pointer', color: '#555', opacity: 0.8 }}
                    title="Download"
                    onClick={() => handleDownloadFile(file)}
                  />
                  <FaTrash
                    size={12}
                    style={{ cursor: 'pointer', color: '#555', opacity: 0.8 }}
                    title="Delete"
                    onClick={() => handleDeleteFile(file.id)}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {/* File Preview */}
      {previewUrl && (
        <div className="file-preview-container">
          <h4 style={{ margin: '12px 0' }}>미리보기</h4>

          {previewType === 'image' ? (
            <img
              src={previewUrl}
              alt="미리보기 이미지"
              style={{ maxWidth: '100%', maxHeight: '600px', objectFit: 'contain' }}
            />
          ) : previewType === 'pdf' ? (
            <iframe
              src={previewUrl}
              title="미리보기 PDF"
              width="100%"
              height="600px"
              style={{ border: '1px solid #ccc' }}
            />
          ) : (
            <p>미리보기를 지원하지 않는 형식입니다.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default MaterialFileSection;
